/* eslint-disable prettier/prettier */
import type React from 'react';
import { useState } from 'react';

import { DetailsTrainingItem, DetailsTrainingResponse } from '@/app/models/detailsTraining';
import { useUndoUnionTrainingMutation } from '@/app/services/detailsTrainingApi';
import { formatDate, formatDateMonthYear } from '@/app/utils/dateUtils';
import formatterMonetaryValue, { formatHandleCPFAndCNPJ } from '@/app/utils/numberUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import TruncatedTextWithTooltip from '@/components/common/truncatedTextWithTooltip';
import { usePeriod } from '@/context/PeriodContext';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Checkbox, IconButton, Menu, MenuItem, TableCell, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface TrainingTableProps {
  selectTraining: number[];
  setSelectTraining: (id: number[]) => void;
  setDrawerEdit: CallableFunction;
  setDrawerTransfer: CallableFunction;
  setSelectedTrainingItem: (id: DetailsTrainingItem) => void;
  selectedTrainingItem: DetailsTrainingItem;
  equipmentData: DetailsTrainingResponse | undefined;
}

export default function TrainingTable({
  selectTraining,
  setSelectTraining,
  setDrawerEdit,
  setDrawerTransfer,
  setSelectedTrainingItem,
  selectedTrainingItem,
  equipmentData,
}: TrainingTableProps) {
  const theme = useTheme();
  const period = usePeriod();

  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [idUndoUnion, setIdUndoUnion] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { t } = useTranslation(['training', 'common']);

  const handleCheckboxChange = (id: number, checked: boolean) => {
    const newSelectedCollaborators = checked
      ? [...selectTraining, id]
      : selectTraining.filter((collabId) => collabId !== id);

    setSelectTraining(newSelectedCollaborators);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = equipmentData?.items?.map((collab) => collab.id);
      setSelectTraining(allIds as number[]);
    } else {
      setSelectTraining([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrowersEdit = () => {
    setDrawerEdit(true);
    setAnchorEl(null);
  };

  const handleDrowersTrasnfer = () => {
    setDrawerTransfer(true);
    setAnchorEl(null);
  };

  const [undoUnion] = useUndoUnionTrainingMutation();

  const handleUndoUnion = async () => {
    await toast.promise(
      undoUnion({
        id: idUndoUnion as number,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:undo.union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setAnchorEl(null);
  };

  const columns: Column[] = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={selectTraining.length > 0 && selectTraining.length < (equipmentData?.items?.length as number)}
          checked={
            (equipmentData?.items?.length as number) > 0 && selectTraining.length === equipmentData?.items?.length
          }
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(6),
    },
    { key: 'supplier', label: t('common:supplier'), width: theme.spacing(30) },
    { key: 'supplierCode', label: t('common:cnpj'), width: theme.spacing(20) },
    { key: 'value', label: t('common:total.value'), width: theme.spacing(20) },
    { key: 'expenseDate', label: t('common:expenseDate') },
    { key: 'objective', label: t('common:objective'), width: theme.spacing(20) },
    { key: 'subject', label: t('subject'), width: theme.spacing(20) },
    { key: 'period', label: t('common:period'), width: theme.spacing(20) },
    { key: 'hour.load', label: t('hour.load'), width: theme.spacing(15) },
    { key: 'activity', label: t('activity'), width: theme.spacing(10) },
    { key: 'consolidated', label: t('common:consolidated'), align: 'center', width: theme.spacing(10) },
    { key: 'grouped', label: t('common:grouped'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (item: DetailsTrainingItem) => {
    const isSelected = selectTraining.includes(item.id);
    return (
      <>
        <TableCell sx={{ ...bodyCellSx, padding: 'checkbox' }}>
          <Checkbox
            color="secondary"
            sx={{ p: 0 }}
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
            inputProps={{ 'aria-labelledby': `Item-${item.id}` }}
          />
        </TableCell>

        <TableCell sx={bodyCellSx}>
          <TruncatedTextWithTooltip value={`${item.supplier ? item.supplier : '---'}`} sx={bodyCellSx} size={32} />
        </TableCell>
        <TableCell sx={{ ...bodyCellSx }}>{formatHandleCPFAndCNPJ(item.supplierCode)}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{formatterMonetaryValue(item.value)}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{item.expenseDate ? formatDate(item.expenseDate) : '----'}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip value={`${item.objective ? item.objective : '---'}`} sx={bodyCellSx} size={15} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip value={`${item.theme ? item.theme : '---'}`} sx={bodyCellSx} size={15} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          {item.initDate || item.endDate
            ? `
            ${item.initDate ? formatDateMonthYear(item.initDate) : ''} - ${
                item.endDate ? formatDateMonthYear(item.endDate as string) : ''
              }`
            : '---'}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{item.hourDuration ? item.hourDuration : '---'}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip value={`${item.activities ? item.activities : '---'}`} sx={bodyCellSx} size={20} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.consolidated ? (
            <CheckIcon sx={{ color: theme.palette.success.light, cursor: 'pointer' }} />
          ) : (
            <CloseIcon sx={{ color: theme.palette.grey[500], cursor: 'pointer' }} />
          )}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.grouped ? (
            <Tooltip title={t('common:union.result')} arrow placement="bottom-start">
              <LinkIcon sx={{ color: theme.palette.secondary.light, cursor: 'pointer' }} />
            </Tooltip>
          ) : null}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              handleMenuOpen(e, item.id);
              setSelectedTrainingItem(item);
              setIdUndoUnion(item.id);
            }}
            aria-label="more actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </>
    );
  };

  // Use the project ID as the unique key
  const getRowKey = (project: DetailsTrainingItem) => project.id as number;

  return (
    <>
      <DataTable
        columns={columns}
        data={equipmentData?.items || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        size="small"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedStatus && (
          <>
            <MenuItem onClick={() => handleDrowersEdit()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon />
                {t('common:edit.detail')}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleDrowersTrasnfer()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareArrowsIcon />
                {t('common:transfer')}
              </Box>
            </MenuItem>
            {selectedTrainingItem.grouped && (
              <MenuItem onClick={() => handleUndoUnion()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CallSplitIcon />
                  {t('common:undo.union')}
                </Box>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
}
